import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import type { AdminSeriesItem, CreationSubmission, EpisodeItem, ReelClip, SeriesItem, VoteResponse } from '../types/content';

function asString(value: unknown, fallback = ''): string {
  const text = value == null ? '' : String(value).trim();
  return text || fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number') return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map((entry) => String(entry)).filter(Boolean) : [];
}

export function mapReelClip(
  doc: QueryDocumentSnapshot<DocumentData>,
  social?: DocumentData,
  seriesTitleById: Record<string, string> = {},
): ReelClip | null {
  const data = doc.data();
  const clipUrl = asString(data.mergedVideoUrl);
  const publishedEpisodeId = asString(data.publishedEpisodeId);
  if (!clipUrl || !publishedEpisodeId) return null;

  const seriesId = asString(data.seriesId);
  const embeddedSeriesTitle = asString(data.seriesTitle);
  const episodeNumber = asNumber(data.episodeNumber);
  const seriesTitle = embeddedSeriesTitle || seriesTitleById[seriesId] || '';
  const fallbackTitle = seriesTitle || (episodeNumber > 0 ? `Episode ${episodeNumber}` : 'Episode');

  return {
    id: doc.id,
    publishedEpisodeId,
    seriesId,
    seriesTitle,
    title: asString(data.title, fallbackTitle),
    description: asString(data.description),
    clipUrl,
    thumbnailUrl: asString(data.thumbnailUrl, clipUrl),
    creatorName: asString(data.creatorName, 'InstaFlix Studio'),
    episodeNumber,
    likeCount: asNumber(social?.likeCount),
    commentCount: asNumber(social?.commentCount),
    shareCount: asNumber(social?.shareCount),
  };
}

export function mapSeries(doc: QueryDocumentSnapshot<DocumentData>): SeriesItem {
  const data = doc.data();
  const bannerUrl = asString(data.bannerUrl, asString(data.coverImage));
  const posterUrl = asString(data.posterUrl, asString(data.coverImage, bannerUrl));
  return {
    id: doc.id,
    slug: asString(data.slug, doc.id),
    title: asString(data.title, 'Untitled'),
    description: asString(data.description),
    longDescription: asString(data.longDescription, asString(data.description)),
    bannerUrl,
    posterUrl,
    genres: asStringArray(data.genres ?? data.genreTags),
    tags: asStringArray(data.tags),
    maturityRating: asString(data.maturityRating),
    totalSeasons: asNumber(data.totalSeasons, 1),
    status: asString(data.status, 'draft'),
  };
}

export function mapAdminSeries(doc: QueryDocumentSnapshot<DocumentData>, episodeCount: number): AdminSeriesItem {
  const data = doc.data();
  const item = mapSeries(doc);
  return {
    ...item,
    episodeCount,
    author: asString(
      data.author ??
        data.creator ??
        data.creatorName ??
        data.studioName ??
        data.createdByName ??
        data.createdByEmail ??
        data.createdBy,
      'InstaFlix Studio',
    ),
  };
}

export function mapEpisode(doc: QueryDocumentSnapshot<DocumentData>): EpisodeItem {
  const data = doc.data();
  return {
    id: doc.id,
    seriesId: asString(data.seriesId),
    seasonId: asString(data.seasonId),
    seasonNumber: asNumber(data.seasonNumber),
    episodeNumber: asNumber(data.episodeNumber),
    title: asString(data.title, 'Episode'),
    description: asString(data.description),
    thumbnailUrl: asString(data.thumbnailUrl),
    videoUrl: asString(data.videoUrl),
    duration: asNumber(data.duration),
  };
}

export function mapCreation(doc: QueryDocumentSnapshot<DocumentData>): CreationSubmission {
  const data = doc.data();
  return {
    id: doc.id,
    uid: asString(data.uid),
    email: asString(data.email),
    displayName: asString(data.displayName),
    title: asString(data.title),
    logline: asString(data.logline),
    genre: asString(data.genre),
    format: asString(data.format),
    referenceUrl: asString(data.referenceUrl),
    status: asString(data.status, 'new') as CreationSubmission['status'],
    adminNote: asString(data.adminNote),
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

export function mapVoteResponse(doc: QueryDocumentSnapshot<DocumentData>): VoteResponse {
  const data = doc.data();
  return {
    id: doc.id,
    uid: asString(data.uid),
    email: asString(data.email),
    displayName: asString(data.displayName),
    usage: asString(data.usage),
    feature: asString(data.feature),
    suggestion: asString(data.suggestion),
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}
